import React, { useState, useEffect } from 'react'
import { Box, TextField, Typography, IconButton, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/Info'
import { useNotify } from 'react-admin'
import { getCompanyEmailDomains, addCompanyEmailDomain, deleteCompanyEmailDomain } from 'api/company'
import { Label } from 'common/RoundedPanel'
import SmallButton from 'components/SmallButton'

interface EmailDomainsManagementProps {
  companyId: string
}

const EmailDomainsManagement: React.FC<EmailDomainsManagementProps> = ({ companyId }) => {
  const [emailDomains, setEmailDomains] = useState<string[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [loading, setLoading] = useState(true)
  const notify = useNotify()

  const loadEmailDomains = async () => {
    try {
      setLoading(true)
      const domains = await getCompanyEmailDomains(companyId)
      setEmailDomains(domains)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to load email domains'
      notify(errorMessage, { type: 'error' })
      console.error('Error loading email domains:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadEmailDomains()
  }, [companyId])

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return

    try {
      await addCompanyEmailDomain(companyId, newDomain.trim())
      setNewDomain('')
      notify('Email domain added successfully', { type: 'success' })
      await loadEmailDomains()
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to add email domain'
      notify(errorMessage, { type: 'error' })
      console.error('Error adding email domain:', err)
    }
  }

  const handleDeleteDomain = async (domain: string) => {
    try {
      await deleteCompanyEmailDomain(companyId, domain)
      notify('Email domain deleted successfully', { type: 'success' })
      await loadEmailDomains()
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to delete email domain'
      notify(errorMessage, { type: 'error' })
      console.error('Error deleting email domain:', err)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddDomain()
    }
  }

  if (loading) {
    return <Typography>Loading email domains...</Typography>
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Label>Company email domains</Label>
        <Tooltip title="If new users register with an email address within these domains, they will be automatically attached to this company">
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box height={8} />

      {/* List existing domains */}
      {emailDomains.map((domain) => (
        <Box
          key={domain}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ 
            mb: 1,
            p: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#f9f9f9'
          }}
        >
          <Typography variant="body2">{domain}</Typography>
          <IconButton
            size="small"
            onClick={() => handleDeleteDomain(domain)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {/* Add new domain */}
      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
        <TextField
          size="small"
          placeholder="Enter email domain"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyDown={handleKeyPress}
          sx={{ flexGrow: 1 }}
        />
        <Box sx={{ mt: '10px' }}>
          <SmallButton
            onClick={handleAddDomain}
            disabled={!newDomain.trim()}
          >
            <AddIcon fontSize="small" />
          </SmallButton>
        </Box>
      </Box>
    </Box>
  )
}

export default EmailDomainsManagement
